import { distinctId } from "./analytics";
import { baseUrl } from "./config";
import type { FeedbackRequest, OverallFeedbackRequest } from "./types";

/**
 * The POST endpoints, each with the body it takes.
 *
 * Keyed by path so the pairing is checked: passing a feedback body to the
 * overall endpoint, or dropping a field the server requires, is a typecheck
 * failure rather than a request the server quietly rejects. Nothing here can
 * check these against the server itself (see "Wire contract" in the README),
 * but it keeps the two call sites honest about which shape goes where.
 */
type PostBodies = {
  "/api/feedback": FeedbackRequest;
  "/api/overall-feedback": OverallFeedbackRequest;
};

function apiHeaders(json = false): HeadersInit {
  const headers: Record<string, string> = {
    "x-distinct-id": distinctId(),
  };
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

/**
 * GETs from the API and hands back the response untouched.
 *
 * Unlike {@link postJson} this reports nothing itself. The one caller is
 * streaming, and it needs the status, the body and any thrown error to tell a
 * failed request from an empty one. It lives here anyway, so that the `baseUrl`
 * join and the headers are decided in one file.
 */
export function apiGet(path: string, signal?: AbortSignal): Promise<Response> {
  return fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers: apiHeaders(),
    signal,
  });
}

/**
 * POSTs JSON and reports whether the server accepted it.
 *
 * Uses `res.ok` rather than testing for 200: both feedback endpoints return
 * nothing, which h3 sends as 204, so an equality check against 200 reports
 * failure on every success.
 */
export async function postJson<P extends keyof PostBodies>(
  path: P,
  body: PostBodies[P],
): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: apiHeaders(true),
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch (e) {
    console.warn(`[ai-search] POST ${path} failed`, e);
    return false;
  }
}
